import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import PhoneNumberField from "./PhoneNumberField";
import ChatBotOption from "./ChatBotOption";
import {
  closeSetMpinModal,
  openLoginModal,
  openThanksPopup,
} from "../../features/slices/ModalSlice";
import { updateSelectedItem } from "../../features/slices/CommonSlice";
import {
  selectMpinDigits,
  selectRegistrationDefaults,
} from "../../features/slices/AppConfigSlice";
import {
  relogin,
  resetMpin,
  sendResetMpinOtp,
  setupMpin,
} from "../../features/slices/UserSlice";
import { formatRegisterPhone } from "../../utils/buildRegisterPayload";
import { hasAuthToken } from "../../utils/authToken";
import {
  isPhoneNumberAcceptable,
  parsePhoneInput,
} from "../../utils/phoneValidation";
import { createEmptyPin } from "../../utils/registrationConfig";

const OTP_DIGITS = 4;
const emptyOtp = () => new Array(OTP_DIGITS).fill("");

function maskPhone(phone) {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length <= 4) return phone;
  return `****${digits.slice(-4)}`;
}

function SetMpinContent({
  mode,
  resetStep,
  localError,
  userError,
  mpinDigits,
  mpin,
  setMpin,
  confirmMpin,
  setConfirmMpin,
  otp,
  setOtp,
  password,
  setPassword,
  showPassword,
  onTogglePassword,
  phone,
  defaultCountry,
  isPhoneValid,
  otpSentMessage,
  isSendingOtp,
  onPhoneChange,
  onSendOtp,
  onResendOtp,
  onSubmit,
  onSwitchMode,
}) {
  const handlePinChange = (element, index, pin, setPin) => {
    const actualValue = element.value;
    const value = element.value.replace(/[^0-9]/g, "");
    pin[index] = value;
    setPin([...pin]);

    if (!actualValue.length) {
      if (element.previousSibling) element.previousSibling.focus();
      return;
    }

    if (value.length && element.nextSibling) element.nextSibling.focus();
  };

  const renderPinInputs = (pin, setPin, inputClassName = "set-mpin-modal__pin-input") =>
    pin.map((data, index) => (
      <input
        className={inputClassName}
        type="password"
        inputMode="numeric"
        name="mpin"
        maxLength="1"
        key={index}
        value={data}
        onChange={(e) => handlePinChange(e.target, index, pin, setPin)}
        onFocus={(e) => e.target.select()}
      />
    ));

  const isResetMode = mode === "reset";
  const isPhoneStep = isResetMode && resetStep === "phone";
  const isVerifyStep = isResetMode && resetStep === "verify";

  return (
    <>
      <div className="set-mpin-modal__chat-banner">
        <ChatBotOption />
      </div>

      <div className="set-mpin-modal__pin-panel">
        <p className="set-mpin-modal__panel-title">
          {isResetMode ? "Reset MPIN" : "Setup MPIN"}
        </p>
        <p className="set-mpin-modal__panel-subtitle">
          {isPhoneStep
            ? "Enter your registered phone number to receive an OTP"
            : isVerifyStep
              ? `Enter the OTP, your account password, and choose a new ${mpinDigits}-digit MPIN`
              : `Please enter a ${mpinDigits}-digit MPIN`}
        </p>

        {isPhoneStep && (
          <>
            <p className="set-mpin-modal__field-label">Phone Number</p>
            <div className="set-mpin-modal__phone-field">
              <PhoneNumberField
                defaultCountry={defaultCountry}
                value={phone}
                onChange={onPhoneChange}
                placeholder="Enter phone number"
              />
            </div>
          </>
        )}

        {isVerifyStep && (
          <>
            {otpSentMessage && (
              <p className="set-mpin-modal__otp-message">{otpSentMessage}</p>
            )}
            <p className="set-mpin-modal__field-label">OTP</p>
            <div className="set-mpin-modal__pin-row">
              {renderPinInputs(otp, setOtp, "set-mpin-modal__otp-input")}
            </div>
            <button
              type="button"
              className="set-mpin-modal__resend-btn"
              onClick={onResendOtp}
              disabled={isSendingOtp}
            >
              {isSendingOtp ? "Sending OTP..." : "Resend OTP"}
            </button>

            <p className="set-mpin-modal__field-label">Password</p>
            <div className="set-mpin-modal__password-field">
              <input
                className="set-mpin-modal__password-input"
                type={showPassword ? "text" : "password"}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your account password"
                autoComplete="current-password"
              />
              <i
                className={`bi ${showPassword ? "bi-eye" : "bi-eye-slash"}`}
                onClick={onTogglePassword}
                aria-label={showPassword ? "Hide password" : "Show password"}
              />
            </div>

            <p className="set-mpin-modal__field-label">New MPIN</p>
            <div className="set-mpin-modal__pin-row">{renderPinInputs(mpin, setMpin)}</div>

            <p className="set-mpin-modal__field-label">Confirm MPIN</p>
            <div className="set-mpin-modal__pin-row">
              {renderPinInputs(confirmMpin, setConfirmMpin)}
            </div>
          </>
        )}

        {!isResetMode && (
          <>
            <p className="set-mpin-modal__field-label">MPIN</p>
            <div className="set-mpin-modal__pin-row">{renderPinInputs(mpin, setMpin)}</div>

            <p className="set-mpin-modal__field-label">Confirm MPIN</p>
            <div className="set-mpin-modal__pin-row">
              {renderPinInputs(confirmMpin, setConfirmMpin)}
            </div>
          </>
        )}
      </div>

      {(localError || userError) && (
        <p className="set-mpin-modal__error">{localError || userError}</p>
      )}

      <button
        type="button"
        className="set-mpin-modal__submit-btn"
        onClick={onSubmit}
        disabled={isPhoneStep && (isSendingOtp || !isPhoneValid)}
      >
        {isPhoneStep
          ? isSendingOtp
            ? "Sending OTP..."
            : "Send OTP"
          : isResetMode
            ? "Reset MPIN"
            : "Setup MPIN"}
      </button>

      <button
        type="button"
        className="set-mpin-modal__mode-toggle"
        onClick={onSwitchMode}
      >
        {isResetMode ? "Set up MPIN instead" : "Reset MPIN"}
      </button>
    </>
  );
}

export default function SetMpinModal() {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const user = useSelector((state) => state.user);
  const registrationDefaults = useSelector(selectRegistrationDefaults);
  const mpinDigits = useSelector(selectMpinDigits);
  const fromMenu = useSelector((state) => state.modal.setMpinModalFromMenu);
  const [mode, setMode] = useState("setup");
  const [resetStep, setResetStep] = useState("phone");
  const [mpin, setMpin] = useState(() => createEmptyPin(mpinDigits));
  const [confirmMpin, setConfirmMpin] = useState(() => createEmptyPin(mpinDigits));
  const [otp, setOtp] = useState(emptyOtp);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [resetPhone, setResetPhone] = useState("");
  const [defaultCountry, setDefaultCountry] = useState("IN");
  const [otpSentMessage, setOtpSentMessage] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (registrationDefaults?.country) {
      setDefaultCountry(registrationDefaults.country);
    }
  }, [registrationDefaults?.country]);

  useEffect(() => {
    setMpin((prev) =>
      prev.length === mpinDigits ? prev : createEmptyPin(mpinDigits)
    );
    setConfirmMpin((prev) =>
      prev.length === mpinDigits ? prev : createEmptyPin(mpinDigits)
    );
  }, [mpinDigits]);

  const clearForm = () => {
    setMpin(createEmptyPin(mpinDigits));
    setConfirmMpin(createEmptyPin(mpinDigits));
    setOtp(emptyOtp());
    setPassword("");
    setShowPassword(false);
    setPhone("");
    setCountryCode("");
    setIsPhoneValid(false);
    setResetPhone("");
    setResetStep("phone");
    setOtpSentMessage("");
    setLocalError("");
  };

  const goToLogin = () => {
    if (isMobile) {
      dispatch(updateSelectedItem("login"));
      return;
    }
    dispatch(openLoginModal("login"));
  };

  const handleClose = () => {
    dispatch(closeSetMpinModal());
    if (!fromMenu) {
      dispatch(openThanksPopup());
    }
  };

  const handlePhoneChange = (value) => {
    setLocalError("");
    const parsed = parsePhoneInput(value);
    setPhone(parsed.e164);
    setIsPhoneValid(parsed.isValid);
    setCountryCode(parsed.isValid ? parsed.countryCode : "");
  };

  const getFormattedResetPhone = () => {
    if (resetPhone) return resetPhone;
    if (phone && isPhoneNumberAcceptable(phone)) return phone;
    return formatRegisterPhone(countryCode, phone);
  };

  const requestResetMpinOtp = (phoneOverride) => {
    const formattedPhone = phoneOverride || getFormattedResetPhone();

    if (!formattedPhone) {
      setLocalError("Please enter a valid phone number");
      return;
    }

    setIsSendingOtp(true);
    setLocalError("");
    dispatch(sendResetMpinOtp({ phone: formattedPhone }))
      .unwrap()
      .then((response) => {
        const sentPhone = response?.phone || formattedPhone;
        setResetPhone(sentPhone);
        setResetStep("verify");
        setOtpSentMessage(
          response?.data?.message ||
          response?.message ||
          `OTP sent to ${maskPhone(sentPhone)}`
        );
      })
      .catch((error) => {
        setOtpSentMessage("");
        setLocalError(error?.error || "Failed to send OTP");
      })
      .finally(() => {
        setIsSendingOtp(false);
      });
  };

  const handleSendOtp = () => {
    if (!phone || !isPhoneNumberAcceptable(phone)) {
      setLocalError("Please enter a valid phone number");
      return;
    }

    requestResetMpinOtp(phone);
  };

  const handleSwitchMode = () => {
    clearForm();
    if (mode === "setup") {
      setMode("reset");
      return;
    }
    setMode("setup");
  };

  const handleSetupSubmit = () => {
    const mpinValue = mpin.join("");
    const confirmValue = confirmMpin.join("");

    if (mpinValue.length !== mpinDigits || confirmValue.length !== mpinDigits) {
      setLocalError(`Please enter a ${mpinDigits}-digit MPIN`);
      return;
    }

    if (mpinValue !== confirmValue) {
      setLocalError("MPIN and Confirm MPIN should match");
      return;
    }

    if (!hasAuthToken()) {
      setLocalError(
        "Cannot set MPIN: no auth token was returned by register or verify-otp. The MPIN API requires Authorization: Bearer <token> — ask backend to return access_token after OTP verify (or provide a login API)."
      );
      return;
    }

    setLocalError("");
    dispatch(setupMpin({ mpin: mpinValue }))
      .unwrap()
      .then(() => handleClose())
      .catch((error) => {
        setLocalError(
          error?.error ||
          (error?.code === "AUTH_REQUIRED"
            ? "Authentication required. Please verify OTP again."
            : "Failed to setup MPIN")
        );
      });
  };

  const handleResetSubmit = () => {
    if (resetStep === "phone") {
      handleSendOtp();
      return;
    }

    const otpValue = otp.join("");
    const mpinValue = mpin.join("");
    const confirmValue = confirmMpin.join("");

    if (otpValue.length !== OTP_DIGITS) {
      setLocalError(`Please enter the ${OTP_DIGITS}-digit OTP`);
      return;
    }

    if (!password.trim()) {
      setLocalError("Please enter your account password");
      return;
    }

    if (mpinValue.length !== mpinDigits || confirmValue.length !== mpinDigits) {
      setLocalError(`Please enter a ${mpinDigits}-digit new MPIN`);
      return;
    }

    if (mpinValue !== confirmValue) {
      setLocalError("New MPIN and Confirm MPIN should match");
      return;
    }

    if (!hasAuthToken()) {
      setLocalError("Authentication required. Please log in again.");
      return;
    }

    setLocalError("");
    dispatch(
      resetMpin({
        mpin: mpinValue,
        password: password.trim(),
        otp: otpValue,
        authMethod: "otp",
      })
    )
      .unwrap()
      .then((response) => {
        clearForm();
        dispatch(closeSetMpinModal());
        dispatch(
          openThanksPopup({
            title: "MPIN Reset Successful",
            message:
              response?.message ||
              "Your MPIN has been reset. Please log in with your new MPIN.",
            redirectToLogin: true,
          })
        );
        // The MPIN credential just changed, so the current session is dropped.
        dispatch(relogin());
        // Send the player back to login right away instead of waiting for the
        // success popup to close, so they are never left on a signed-out page.
        goToLogin();
      })
      .catch((error) => {
        setLocalError(
          error?.error ||
          (error?.code === "AUTH_REQUIRED"
            ? "Authentication required. Please log in again."
            : "Failed to reset MPIN")
        );
      });
  };

  const handleSubmit =
    mode === "reset" ? handleResetSubmit : handleSetupSubmit;

  const contentProps = {
    mode,
    resetStep,
    localError,
    userError: user.error,
    mpinDigits,
    mpin,
    setMpin,
    confirmMpin,
    setConfirmMpin,
    otp,
    setOtp,
    password,
    setPassword,
    showPassword,
    onTogglePassword: () => setShowPassword((prev) => !prev),
    phone,
    defaultCountry,
    isPhoneValid,
    otpSentMessage,
    isSendingOtp,
    onPhoneChange: handlePhoneChange,
    onSendOtp: handleSendOtp,
    onResendOtp: () => requestResetMpinOtp(),
    onSubmit: handleSubmit,
    onSwitchMode: handleSwitchMode,
  };

  const modalTitle = mode === "reset" ? "Reset MPIN" : "Set MPIN";

  if (isMobile) {
    return (
      <div className="set-mpin-modal set-mpin-modal--mobile">
        <div className="set-mpin-modal__mobile-shell">
          <div className="set-mpin-modal__header">
            <h2 className="set-mpin-modal__title">{modalTitle}</h2>
            <i
              className="bi bi-x-lg set-mpin-modal__close"
              onClick={handleClose}
              aria-label="Close"
            />
          </div>
          <div className="set-mpin-modal__body">
            <SetMpinContent {...contentProps} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="modal fade set-mpin-modal show"
      style={{
        display: "block",
        backdropFilter: "blur(15px)",
        background: "rgb(70, 70, 70, 0.6)",
      }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title set-mpin-modal__title">{modalTitle}</h2>
            <i
              data-bs-dismiss="modal"
              aria-label="Close"
              className="bi bi-x-lg set-mpin-modal__close"
              onClick={handleClose}
            />
          </div>
          <div className="modal-body set-mpin-modal__body">
            <SetMpinContent {...contentProps} />
          </div>
        </div>
      </div>
    </div>
  );
}
